import axios, { AxiosResponse } from 'axios';

import config from '@/config';
import store from '@/store';
import ApiList, { ApiListResponse } from '@/api/list';
import { isUndefined } from 'lodash';

export class Language {
  public static readonly DEFAULT: string = config.LANGUAGE;

  // list of languages we consider the Content creator can generate content for
  // Based on ISO-639-1
  public static readonly LIST: string[] = [
    'en',
    'vi',
    'ja',
    'de',
    'br',
    'pt',
    'ko',
    'th',
    'hi',
    'ind',
    'es',
    'fr',
    'tr',
    'it',
    'zh_CN',
    'zh_TW'
  ];

  // language codes -> Country codes (to select the flag)
  public static flagOf(language: string): string {
    switch (language) {
      case 'vi':
        return 'vn';
      case 'ja':
        return 'jp';
      case 'de':
        return 'DE';
      case 'en':
        return 'us';
      case 'pt':
        return 'pt';
      case 'br':
        return 'br';
      case 'ko':
        return 'kr';
      case 'hi':
        return 'in';
      case 'ind':
        return 'id';
      case 'fr':
        return 'fr';
      case 'tr':
        return 'tr';
      case 'it':
        return 'it';
      case 'zh_CN':
        return 'zh_CN';
      case 'zh_TW':
        return 'zh_TW';
      default:
        return language;
    }
  }

  // text shown to the user
  public static codeOf(language: string): string {
    switch (language) {
      case 'vi':
        return 'VI';
      case 'ja':
        return 'JA';
      case 'de':
        return 'DE';
      case 'pt':
        return 'PT';
      case 'br':
        return 'PT';
      case 'ko':
        return 'KO';
      case 'th':
        return 'THAI';
      case 'hi':
        return 'HINDI';
      case 'ind':
        return 'INDO';
      case 'es':
        return 'ES';
      case 'fr':
        return 'FR';
      case 'tr':
        return 'Turkish';
      case 'it':
        return 'Italian';
      case 'zh_CN':
        return 'Chinese (S)';
      case 'zh_TW':
        return 'Chinese (T)';
      default:
        return 'EN';
    }
  }
}

export class InternationalizedField<T> {
  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public en?: T = undefined;
  public vi?: T = undefined;
  public ja?: T = undefined;
  public de?: T = undefined;
  public pt?: T = undefined;
  public br?: T = undefined;
  public fr?: T = undefined;
  public tr?: T = undefined;
  public it?: T = undefined;
  public zh_CN?: T = undefined;
  public zh_TW?: T = undefined;

  public constructor(values?: InternationalizedField<T> | undefined | null) {
    if (values) {
      Language.LIST.forEach((lang) => {
        if (lang in values) {
          this.set(lang, (values as any)[lang] as T);
        }
      });
    }
  }

  public get(language: string): T | undefined {
    if (language in this) {
      return (this as any)[language];
    } else {
      return undefined;
    }
  }

  public set(language: string, value?: T) {
    (this as any)[language] = value;
  }

  public getDefaultValue(): T | undefined {
    if (Language.DEFAULT in this) {
      return this.get(Language.DEFAULT);
    } else {
      return undefined;
    }
  }
}

export class ExerciseGameType {
  public static readonly CONVERSATION: string = 'CONVERSATION';
  public static readonly CONVERSATION_FREE: string = 'CONVERSATION_FREE';
  public static readonly PRONUNCIATION: string = 'PRONUNCIATION';
  public static readonly WORD_STRESS: string = 'WORD_STRESS';
  public static readonly LISTEN_AUDIO2TEXT: string = 'LISTEN_AUDIO2TEXT';
  public static readonly LISTEN_TEXT2AUDIO: string = 'LISTEN_TEXT2AUDIO';
  public static readonly SENTENCE_STRESS: string = 'SENTENCE_STRESS';
  public static readonly VIDEO_CONVERSATION: string = 'VIDEO_CONVERSATION';
  public static readonly PICK_YOUR_ANSWER: string = 'PICK_YOUR_ANSWER';
  public static readonly INSIGHT_ONLY: string = 'INSIGHT_ONLY';
  public static readonly VIDEO_ONLY: string = 'VIDEO_ONLY';
  public static readonly MISSING_CHARACTER: string = 'MISSING_CHARACTER';
  public static readonly UNSCRAMBLE_WORD: string = 'UNSCRAMBLE_WORD';

  public static readonly CONVERSATION_PRESENTATION: string = 'Conversation';
  public static readonly CONVERSATION_FREE_PRESENTATION: string =
    'Conversation Free';
  public static readonly PRONUNCIATION_PRESENTATION: string = 'Pronunciation';
  public static readonly WORD_STRESS_PRESENTATION: string = 'Word Stress';
  public static readonly LISTEN_AUDIO2TEXT_PRESENTATION: string =
    'Audio to Text';
  public static readonly LISTEN_TEXT2AUDIO_PRESENTATION: string =
    'Text to Audio';
  public static readonly SENTENCE_STRESS_PRESENTATION: string =
    'Sentence Stress';
  public static readonly VIDEO_CONVERSATION_PRESENTATION: string =
    'Video Conversation';
  public static readonly PICK_YOUR_ANSWER_PRESENTATION: string =
    'Pick your answer';
  public static readonly INSIGHT_ONLY_PRESENTATION: string = 'Insight Only';
  public static readonly VIDEO_ONLY_PRESENTATION: string = 'Video Only';
  public static readonly MISSING_CHARACTER_PRESENTATION: string =
    'Missing Character';
  public static readonly UNSCRAMBLE_WORD_PRESENTATION: string =
    'Unscramble Word';

  public static readonly CONVERSATION_SLUG: string = 'conversation';
  public static readonly CONVERSATION_FREE_SLUG: string = 'conversation-free';
  public static readonly PRONUNCIATION_SLUG: string = 'pronunciation';
  public static readonly WORD_STRESS_SLUG: string = 'word-stress';
  public static readonly LISTEN_AUDIO2TEXT_SLUG: string = 'audio-text';
  public static readonly LISTEN_TEXT2AUDIO_SLUG: string = 'text-audio';
  public static readonly SENTENCE_STRESS_SLUG: string = 'sentence-stress';
  public static readonly VIDEO_CONVERSATION_SLUG: string = 'video-conversation';
  public static readonly PICK_YOUR_ANSWER_SLUG: string = 'pick-your-answer';
  public static readonly INSIGHT_ONLY_SLUG: string = 'insight';
  public static readonly VIDEO_ONLY_SLUG: string = 'video';
  public static readonly MISSING_CHARACTER_SLUG: string = 'missing-character';
  public static readonly UNSCRAMBLE_WORD_SLUG: string = 'unscramble-word';

  public static readonly OPTIONS = [
    ExerciseGameType.CONVERSATION,
    ExerciseGameType.CONVERSATION_FREE,
    ExerciseGameType.PRONUNCIATION,
    ExerciseGameType.WORD_STRESS,
    ExerciseGameType.SENTENCE_STRESS,
    ExerciseGameType.LISTEN_TEXT2AUDIO,
    ExerciseGameType.LISTEN_AUDIO2TEXT,
    ExerciseGameType.VIDEO_CONVERSATION,
    ExerciseGameType.PICK_YOUR_ANSWER,
    ExerciseGameType.INSIGHT_ONLY,
    ExerciseGameType.VIDEO_ONLY,
    ExerciseGameType.MISSING_CHARACTER,
    ExerciseGameType.UNSCRAMBLE_WORD
  ];

  public static readonly SLUGS = [
    ExerciseGameType.CONVERSATION_SLUG,
    ExerciseGameType.CONVERSATION_FREE_SLUG,
    ExerciseGameType.PRONUNCIATION_SLUG,
    ExerciseGameType.WORD_STRESS_SLUG,
    ExerciseGameType.SENTENCE_STRESS_SLUG,
    ExerciseGameType.LISTEN_TEXT2AUDIO_SLUG,
    ExerciseGameType.LISTEN_AUDIO2TEXT_SLUG,
    ExerciseGameType.VIDEO_CONVERSATION_SLUG,
    ExerciseGameType.PICK_YOUR_ANSWER_SLUG,
    ExerciseGameType.INSIGHT_ONLY_SLUG,
    ExerciseGameType.VIDEO_ONLY_SLUG,
    ExerciseGameType.MISSING_CHARACTER_SLUG,
    ExerciseGameType.UNSCRAMBLE_WORD_SLUG
  ];

  public static readonly PRESENTATION = [
    ExerciseGameType.CONVERSATION_PRESENTATION,
    ExerciseGameType.CONVERSATION_FREE_PRESENTATION,
    ExerciseGameType.PRONUNCIATION_PRESENTATION,
    ExerciseGameType.WORD_STRESS_PRESENTATION,
    ExerciseGameType.SENTENCE_STRESS_PRESENTATION,
    ExerciseGameType.LISTEN_TEXT2AUDIO_PRESENTATION,
    ExerciseGameType.LISTEN_AUDIO2TEXT_PRESENTATION,
    ExerciseGameType.VIDEO_CONVERSATION_PRESENTATION,
    ExerciseGameType.PICK_YOUR_ANSWER_PRESENTATION,
    ExerciseGameType.INSIGHT_ONLY_PRESENTATION,
    ExerciseGameType.VIDEO_ONLY_PRESENTATION,
    ExerciseGameType.MISSING_CHARACTER_PRESENTATION,
    ExerciseGameType.UNSCRAMBLE_WORD_PRESENTATION
  ];

  public static slugToType(slug: string) {
    return ExerciseGameType.OPTIONS[ExerciseGameType.SLUGS.indexOf(slug)];
  }

  public static typeToSlug(game_type: string) {
    const index = ExerciseGameType.OPTIONS.indexOf(game_type);
    if (index > -1) {
      return ExerciseGameType.SLUGS[index];
    } else {
      return '';
    }
  }

  public static typeToPresentation(game_type: string) {
    const index = ExerciseGameType.OPTIONS.indexOf(game_type);
    if (index > -1) {
      return ExerciseGameType.PRESENTATION[index];
    } else {
      return '';
    }
  }

  public static regex() {
    return this.SLUGS.join('|');
  }
}
export class LessonGameType {
  public static readonly IELTS: string = 'IELTS';
  public static readonly CONVERSATION: string = 'CONVERSATION';
  public static readonly CONVERSATION_FREE: string = 'CONVERSATION_FREE';
  public static readonly PRONUNCIATION: string = 'PRONUNCIATION';
  public static readonly SENTENCE_STRESS: string = 'SENTENCE_STRESS';
  public static readonly LISTEN: string = 'LISTEN';
  public static readonly ROLE_PLAY: string = 'ROLE_PLAY';
  public static readonly WORD_STRESS: string = 'WORD_STRESS';
  public static readonly VIDEO_CONVERSATION: string = 'VIDEO_CONVERSATION';
  public static readonly READING: string = 'READING';
  public static readonly PICK_YOUR_ANSWER: string = 'PICK_YOUR_ANSWER';
  public static readonly MIXED_GAME: string = 'MIXED_GAME';
  public static readonly PROGRAM_INSIGHT: string = 'PROGRAM_INSIGHT';
  public static readonly PROGRAM_VIDEO: string = 'PROGRAM_VIDEO';
  public static readonly ASSESSMENT: string = 'ASSESSMENT';
  public static readonly VIDEO_ONLY: string = 'VIDEO_ONLY';
  public static readonly MISSING_CHARACTER: string = 'MISSING_CHARACTER';
  public static readonly UNSCRAMBLE_WORD: string = 'UNSCRAMBLE_WORD';

  public static readonly IELTS_PRESENTATION: string = 'IELTS';
  public static readonly CONVERSATION_PRESENTATION: string = 'Conversation';
  public static readonly CONVERSATION_FREE_PRESENTATION: string =
    'Conversation Free';
  public static readonly PRONUNCIATION_PRESENTATION: string = 'Pronunciation';
  public static readonly SENTENCE_STRESS_PRESENTATION: string =
    'Sentence Stress';
  public static readonly LISTEN_PRESENTATION: string = 'Listen';
  public static readonly ROLE_PLAY_PRESENTATION: string = 'Role Play';
  public static readonly WORD_STRESS_PRESENTATION: string = 'Word Stress';
  public static readonly VIDEO_CONVERSATION_PRESENTATION: string =
    'Video Conversation';
  public static readonly READING_PRESENTATIOM: string = 'Reading';
  public static readonly PICK_YOUR_ANSWER_PRESENTATION: string =
    'Pick your answer';
  public static readonly MIXED_GAME_PRESENTATION: string = 'Mixed Game';
  public static readonly PROGRAM_INSIGHT_PRESENTATION: string =
    'Program Insight';
  public static readonly PROGRAM_VIDEO_PRESENTATION: string = 'Program Video';
  public static readonly ASSESSMENT_PRESENTATION: string = 'Assessment';
  public static readonly VIDEO_ONLY_PRESENTATION: string = 'Video Only';
  public static readonly MISSING_CHARACTER_PRESENTATION: string =
    'Missing Character';
  public static readonly UNSCRAMBLE_WORD_PRESENTATION: string =
    'Unscramble Word';

  public static readonly OPTIONS = [
    LessonGameType.IELTS,
    LessonGameType.CONVERSATION,
    LessonGameType.CONVERSATION_FREE,
    LessonGameType.PRONUNCIATION,
    LessonGameType.SENTENCE_STRESS,
    LessonGameType.LISTEN,
    LessonGameType.ROLE_PLAY,
    LessonGameType.WORD_STRESS,
    LessonGameType.VIDEO_CONVERSATION,
    LessonGameType.READING,
    LessonGameType.PICK_YOUR_ANSWER,
    LessonGameType.MIXED_GAME,
    LessonGameType.PROGRAM_INSIGHT,
    LessonGameType.PROGRAM_VIDEO,
    LessonGameType.ASSESSMENT,
    LessonGameType.VIDEO_ONLY,
    LessonGameType.MISSING_CHARACTER,
    LessonGameType.UNSCRAMBLE_WORD
  ];

  public static readonly PRESENTATION = [
    LessonGameType.IELTS_PRESENTATION,
    LessonGameType.CONVERSATION_PRESENTATION,
    LessonGameType.CONVERSATION_FREE_PRESENTATION,
    LessonGameType.PRONUNCIATION_PRESENTATION,
    LessonGameType.SENTENCE_STRESS_PRESENTATION,
    LessonGameType.LISTEN_PRESENTATION,
    LessonGameType.ROLE_PLAY_PRESENTATION,
    LessonGameType.WORD_STRESS_PRESENTATION,
    LessonGameType.VIDEO_CONVERSATION_PRESENTATION,
    LessonGameType.READING_PRESENTATIOM,
    LessonGameType.PICK_YOUR_ANSWER_PRESENTATION,
    LessonGameType.MIXED_GAME_PRESENTATION,
    LessonGameType.PROGRAM_INSIGHT_PRESENTATION,
    LessonGameType.PROGRAM_VIDEO_PRESENTATION,
    LessonGameType.ASSESSMENT_PRESENTATION,
    LessonGameType.VIDEO_ONLY_PRESENTATION,
    LessonGameType.MISSING_CHARACTER_PRESENTATION,
    LessonGameType.UNSCRAMBLE_WORD_PRESENTATION
  ];
}

export class LessonGameSubType {
  public static readonly OPTIONS: string[] = [
    null,
    'linkage_game',
    'drop_game'
  ];
  public static readonly PRESENTATION: string[] = [
    'None',
    'Linkage game',
    'Drop game'
  ];
}
export class LessonSpeechType {
  public static readonly OPTIONS: string[] = [null, 'ielts', 'casual'];
  public static readonly PRESENTATION: string[] = ['None', 'IELTS', 'Casual'];
}

export class ExerciseGameSubType {
  public static readonly OPTIONS: string[] = [
    null,
    'linkage_game',
    'drop_game'
  ];
  public static readonly PRESENTATION: string[] = [
    'None',
    'Linkage game',
    'Drop game'
  ];
}

export class ExerciseScoringMode {
  public static readonly OPTIONS: string[] = ['regular', 'advanced'];
  public static readonly PRESENTATION: string[] = ['Regular', 'Advanced'];
}

export class IntelligibilityReady {
  public static readonly OPTIONS = [true, false];
  public static readonly PRESENTATION = ['True', 'False'];
}

export class Difficulty {
  public static readonly OPTIONS = [
    'EASY',
    'MEDIUM',
    'DIFFICULT',
    'VERY_DIFFICULT'
  ];
  public static readonly PRESENTATION = [
    'Easy',
    'Medium',
    'Difficult',
    'Very Difficult'
  ];
}

export class Prominence {
  public static readonly OPTIONS = ['LOW', 'HIGH'];
  public static readonly PRESENTATION = ['Low', 'High'];
}

export class Duration {
  public static readonly OPTIONS = ['SLOW', 'NORMAL'];
  public static readonly PRESENTATION = ['Slow', 'Normal'];
}

export class MinAccess {
  public static readonly OPTIONS = ['membership', 'old_content', ''];
  public static readonly PRESENTATION = ['Membership', 'Old Content', 'None'];
}

export class Countries {
  public static readonly OPTIONS = [
    'us',
    'vn',
    'jp',
    'de',
    'br',
    'fr',
    'pt',
    'in',
    'th',
    'kr',
    'za',
    'id',
    'sg',
    'cz',
    'dk',
    'nl',
    'tr',
    'it',
    'zh_CN',
    'zh_TW'
  ];
  public static readonly PRESENTATION = [
    'United States',
    'Vietnam',
    'Japan',
    'German',
    'Brazil',
    'French',
    'Portugal',
    'India',
    'Thailand',
    'South Korea',
    'South Africa',
    'Indonesia',
    'Singapore',
    'Czech Republic',
    'Denmark',
    'Netherlands',
    'Turkey',
    'Italian',
    'Chinese (S)',
    'Chinese (T)'
  ];
}

export class Platforms {
  public static readonly OPTIONS = [null, 'iOS', 'Android'];
  public static readonly PRESENTATION = ['All', 'iOS', 'Android'];
}

export class ExtraInfoFields {
  public static readonly OPTIONS = ['drop'];
  public static readonly PRESENTATION = ['drop'];
}

export class Languages {
  /* public static readonly OPTIONS = ['ar', 'bn', 'zh', 'cs', 'nl', 'en', 'fr', 'de', 'hi', 'hu', 'id', 'it', 'ja',
                                        'ko', 'ms', 'nb', 'pl', 'br', 'pt', 'pa', 'ro', 'ru', 'es', 'sv', 'ta', 'te',
                                        'th', 'tr', 'uk', 'vi', 'af', 'sq', 'hy', 'bg', 'my', 'ca', 'hr', 'da', 'et',
                                        'fa', 'fi', 'el', 'he', 'is', 'km', 'la', 'li', 'mk', 'mt', 'ne', 'sk', 'sl',
                                        'so', 'sw', 'tl', 'bo', 'ur', 'uz', 'cy'];*/
  public static readonly PRESENTATION = [
    'Arabic',
    'Bengali',
    'Chinese',
    'Czech',
    'Dutch',
    'English',
    'French',
    'German',
    'Hindi',
    'Hungarian',
    'Indonesian',
    'Italian',
    'Japanese',
    'Korean',
    'Malay',
    'Norwegian',
    'Polish',
    'Portuguese (Brazil)',
    'Portuguese (Portugal)',
    'Punjabi',
    'Romanian',
    'Russian',
    'Spanish',
    'Swedish',
    'Tamil',
    'Telugu',
    'Thai',
    'Turkish',
    'Ukranian',
    'Vietnamese',
    'Afrikaans',
    'Albanian',
    'Armenian',
    'Bulgarian',
    'Burmese',
    'Catalan',
    'Croatian',
    'Danish',
    'Estonian',
    'Farsi',
    'Finnish',
    'Greek',
    'Hebrew',
    'Icelandic',
    'Khmer',
    'Laotian',
    'Lithuanian',
    'Macedonian',
    'Maltese',
    'Nepali',
    'Slovak',
    'Slovenian',
    'Somali',
    'Swahili',
    'Tagalog-Filipino',
    'Tibetan',
    'Urdu',
    'Uzbek',
    'Welsh'
  ];
}

export class Tags {
  public static readonly OPTIONS = [
    '',
    'new',
    'hide',
    'video_initial',
    'influencers',
    'purpose_travel',
    'purpose_job',
    'education',
    'purpose_live_work_abroad',
    'purpose_culture_entertainment',
    'purpose_other',
    'level_beginner',
    'level_intermediate',
    'level_advanced',
    'level_novice',
    'level_upper_beginner',
    'level_lower_intermediate',
    'level_upper_intermediate',
    'mixed_level',
    'ielts',
    'band_5',
    'band_6',
    'band_7',
    'band_8',
    'band_9',
    'oxford',
    'k12',
    'hidden',
    'harper_collins',
    'ielts_book',
    'pearson',
    'pearson_pte',
    'eiken',
    'purpose_exam_preparation',
    'us_citizenship',
    'oup_aviation',
    'pertamina'
  ];
  public static readonly PRESENTATION = [
    'None',
    'New',
    'Hide',
    'Video at intro',
    'Influencers',
    'Purpose: Travel',
    'Purpose: Job',
    'Purpose: Education',
    'Purpose: Live/Work Abroad',
    'Purpose: Culture/Entertainment',
    'Purpose: Other',
    'Level: Beginner',
    'Level: Intermediate',
    'Level: Advanced',
    'Level: Novice',
    'Level: Upper Beginner',
    'Level: Lower Intermediate',
    'Level: Upper Intermediate',
    'Mixed Level',
    'IELTS',
    'Band 5',
    'Band 6',
    'Band 7',
    'Band 8',
    'Band 9',
    'Oxford',
    'K12',
    'Hidden',
    'HarperCollins',
    'Ielts Book',
    'Pearson',
    'Pearson Pte',
    'EIKEN',
    'Purpose: Exam Preparation',
    'US Citizenship',
    'OUP Aviation',
    'Pertamina'
  ];
}

export class ModuleTags {
  public static readonly OPTIONS = [
    null,
    'new_content',
    'new_feature',
    'explore_direct_exercise_1',
    'explore_direct_exercise_2',
    'explore_exercise_background_1',
    'explore_exercise_background_2'
  ];
  public static readonly PRESENTATION = [
    'None',
    'New Content',
    'New Feature',
    'Direct Exercise 1',
    'Direct Exercise 2',
    'Exercise background 1',
    'Exercise background 2'
  ];
}

export class LessonTags {
  public static readonly OPTIONS = ['book_free_lesson'];
  public static readonly PRESENTATION = ['Book Free Lesson'];
}

export class CourseStatusFields {
  public static readonly OPTIONS = ['pending', 'published'];
  public static readonly PRESENTATION = ['Pending', 'Published'];
}

export class CourseAvailabilityFields {
  public static readonly OPTIONS = ['all', 'b2b', 'b2c'];
  public static readonly PRESENTATION = ['ALL', 'B2B', 'B2C'];
}

export class CourseCertificateFields {
  public static readonly OPTIONS = ['course', 'book'];
  public static readonly PRESENTATION = ['Course', 'Book'];
}

export interface IdResponse {
  id?: number;
}

export function filterKeys(data: any, removeKeys: string[]): any {
  if (!removeKeys) {
    return data;
  }

  if (data instanceof Array) {
    data = [...data];
  } else {
    data = { ...data };
  }
  for (const key of removeKeys) {
    delete data[key];
  }

  return data;
}

export function filterUndefined(data: any): any {
  if (data instanceof Array) {
    data = [...data];
  } else {
    data = { ...data };
  }

  for (const key in data) {
    if (!data.hasOwnProperty(key)) {
      continue;
    }
    if (data[key] === undefined) {
      delete data[key];
    } else if (data[key] instanceof Array) {
      for (const index in data[key]) {
        if (data[key][index] instanceof Object) {
          data[key][index] = filterUndefined(
            filterKeys(data[key][index], data[key][index]._filteredFields)
          );
        }
      }
    } else if (data[key] && data[key]._filteredFields) {
      data[key] = filterUndefined(
        filterKeys(data[key], data[key]._filteredFields)
      );
    }
  }

  return data;
}

export interface ConstructorOptions {
  urlBase: string;
  filteredFields?: string[];
}

export interface ListOptions<T extends ApiBase<T>> {
  urlBase: string;
  create: (response: any) => T;
  parameters?: object;
  all?: boolean;
  readFromCache?: () => ApiList<T> | null;
  writeToCache?: (list: ApiList<T>) => Promise<void>;
}

export interface GetOptions<T> {
  urlBase: string;
  create: (response: any) => T;
  id: number | null;
  query?: { [key: string]: string };
}

export abstract class ApiBase<T> {
  private readonly _urlBase?: string;
  public readonly _filteredFields?: string[];

  public id?: any = undefined;

  protected constructor(options: ConstructorOptions) {
    this._urlBase = options.urlBase;
    if (!this._urlBase.endsWith('/')) {
      this._urlBase += '/';
    }

    if (options.filteredFields) {
      this._filteredFields = options.filteredFields;
    } else {
      this._filteredFields = [];
    }

    this._filteredFields.push('_filteredFields');
    this._filteredFields.push('_urlBase');
  }

  /**
   * Copies values from response to this object's attribute.
   * Won't set missing attributes to undefined.
   *
   * @param response
   * @returns {ApiBase<T>}
   */
  public assign?(response: any): ApiBase<T> {
    if (response) {
      for (const key in response) {
        if (response.hasOwnProperty(key) && this.hasOwnProperty(key)) {
          (this as any)[key] = response[key];
        }
      }
    }

    return this;
  }

  protected static async doGet<T>(options: GetOptions<T>): Promise<T> {
    if (!store.state.sessionToken) {
      throw new Error('Not logged in.');
    }

    let urlBase = options.urlBase;
    if (!urlBase.endsWith('/')) {
      urlBase += '/';
    }

    if (options.query) {
      const keys = Object.keys(options.query);
      const params = [];
      keys.forEach((key) => {
        params.push(`${key}=${encodeURI(options.query[key])}`);
      });
      urlBase += '?' + params.join('&');
    }

    if (options.id) {
      const response: AxiosResponse<any> = await axios({
        method: 'GET',
        url: `${urlBase}${options.id}/`,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
      return options.create(response.data);
    } else {
      const response: AxiosResponse<any> = await axios({
        method: 'GET',
        url: `${urlBase}`,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
      return options.create(response.data);
    }
  }

  protected static async doList<T extends ApiBase<T>>(
    options: ListOptions<T>
  ): Promise<ApiList<T>> {
    if (!!options.readFromCache !== !!options.writeToCache) {
      throw new Error(
        'Must specify both or none of readFromCache/writeToCache.'
      );
    }

    if (options.readFromCache && !options.all) {
      throw new Error('Cannot use cache unless all=true.');
    }

    if (!store.state.sessionToken) {
      throw new Error('Not logged in.');
    }

    if (options.readFromCache) {
      const cached = options.readFromCache();
      if (cached) {
        return cached;
      }
    }

    let urlBase = options.urlBase;
    if (!urlBase.endsWith('/')) {
      urlBase += '/';
    }

    const response: AxiosResponse<ApiListResponse<ApiBase<T>>> = await axios({
      method: 'GET',
      url: urlBase,
      params: options.parameters,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });

    const result = new ApiList<T>(response.data, options.create);

    if (options.all) {
      await result.readRemaining();
    }

    if (options.writeToCache) {
      await options.writeToCache(result);
    }

    return result;
  }

  /**
   * Save entity, return id;
   *
   * @returns {Promise<number>}
   */
  public async save?(): Promise<number> {
    if (!store.state.sessionToken) {
      throw new Error('Not logged in.');
    }
    const entity = filterUndefined(filterKeys(this, this._filteredFields));

    let response: AxiosResponse<IdResponse>;
    if ('id' in entity) {
      if (isUndefined(entity.parent_project)) {
        entity.parent_project = null;
      }
      response = await axios({
        method: 'PATCH',
        url: `${this._urlBase}${entity.id}/`,
        data: entity,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
    } else {
      response = await axios({
        method: 'POST',
        url: this._urlBase,
        data: entity,
        headers: {
          'X-Session': store.state.sessionToken
        }
      });
    }

    if (!('id' in response.data) || response.data.id === undefined) {
      throw new Error('Missing id in response.');
    }

    this.assign(response.data);

    return response.data.id;
  }

  public async delete?(): Promise<void> {
    if (!store.state.sessionToken) {
      throw new Error('Not logged in.');
    }

    const entity = filterUndefined(filterKeys(this, this._filteredFields));

    let response: AxiosResponse<IdResponse>;

    response = await axios({
      method: 'DELETE',
      url: `${this._urlBase}${entity.id}/`,
      headers: {
        'X-Session': store.state.sessionToken
      }
    });

    if (response.status !== 204) {
      throw new Error('Unable do delete, please refresh browser');
    }
  }
}
